import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { immer } from 'zustand/middleware/immer'
import { useToast } from '../../utils/ToastContext'
import { trpc } from '../../utils/trpc'

/**
 * @fileoverview PEC Detail Store
 *
 * Zustand store for managing PEC cycle state and mutations
 */

interface PecRatingConfig {
	count: number
	options: { value: string; valueLabel: string; labelColor: string }[]
}

interface PecFormData {
	name: string
	description: string | null
	populationId: string | null
	promptText: string
	ratingScale: number
	ratingConfig: PecRatingConfig | null
	allowNa: boolean
	textFeedbackEnabled: boolean
	emailTemplateId: string | null
	reminderTemplateId: string | null
	reminderDays: number
	reminderLogic: 'until_sent' | 'until_completion' | null
	reminderMaxEmails: number
}

export interface PecData {
	id: string
	name: string
	description: string | null
	status: 'draft' | 'ready' | 'active' | 'closed' | 'archived'
	organizationId: string
	populationId: string | null
	promptText: string
	ratingScale: number
	ratingConfig: PecRatingConfig | null
	allowNa: boolean
	textFeedbackEnabled: boolean
	emailTemplateId: string | null
	reminderTemplateId: string | null
	reminderDays: number
	reminderLogic: 'until_sent' | 'until_completion' | null
	reminderMaxEmails: number
	launchedAt: Date | null
	closedAt: Date | null
	createdAt: Date
	updatedAt: Date
}

interface PecState {
	pecCycle: PecData | null
	isLoading: boolean
	error: string | null

	setPecCycle: (cycle: PecData) => void
	resetPecCycle: () => void
	savePecCycle: () => PecFormData
	updatePecCycle: (data: Partial<PecFormData>) => void
	setError: (error: string | null) => void

	useCreatePecCycle: () => {
		createPecCycle: (data: PecFormData) => Promise<void>
		isLoading: boolean
	}
	useEditPecCycle: (pecCycleId?: string) => {
		editPecCycle: (data: Partial<PecFormData>) => Promise<void>
		isLoading: boolean
	}
	useMarkReadyPecCycle: (pecCycleId: string) => {
		markReadyPecCycle: () => Promise<void>
		isLoading: boolean
	}
	useLaunchPecCycle: (pecCycleId: string) => {
		launchPecCycle: () => Promise<void>
		isLoading: boolean
	}
	useClosePecCycle: (pecCycleId: string) => {
		closePecCycle: () => Promise<void>
		isLoading: boolean
	}
	useArchivePecCycle: (pecCycleId: string) => {
		archivePecCycle: () => Promise<void>
		isLoading: boolean
	}
}

export const usePecStore = create<PecState>()(
	devtools(
		immer((set, get) => ({
			pecCycle: null,
			isLoading: false,
			error: null,

			setPecCycle: (cycle: PecData) =>
				set((state) => {
					state.pecCycle = cycle
				}),

			resetPecCycle: () =>
				set((state) => {
					state.pecCycle = null
					state.error = null
				}),

			updatePecCycle: (data: Partial<PecFormData>) =>
				set((state) => {
					if (state.pecCycle) {
						state.pecCycle = {
							...state.pecCycle,
							...data,
						}
					} else {
						state.pecCycle = {
							id: '',
							name: '',
							description: null,
							status: 'draft',
							organizationId: '',
							populationId: null,
							promptText: '',
							ratingScale: 5,
							ratingConfig: null,
							allowNa: false,
							textFeedbackEnabled: true,
							emailTemplateId: null,
							reminderTemplateId: null,
							reminderDays: 0,
							reminderLogic: null,
							reminderMaxEmails: 0,
							launchedAt: null,
							closedAt: null,
							createdAt: new Date(),
							updatedAt: new Date(),
							...data,
						}
					}
				}),

			setError: (error: string | null) =>
				set((state) => {
					state.error = error
				}),

			savePecCycle: () => {
				const state = get()
				if (!state.pecCycle) throw new Error('No PEC cycle data to save')
				return {
					name: state.pecCycle.name,
					description: state.pecCycle.description,
					populationId: state.pecCycle.populationId,
					promptText: state.pecCycle.promptText,
					ratingScale: state.pecCycle.ratingScale,
					ratingConfig: state.pecCycle.ratingConfig,
					allowNa: state.pecCycle.allowNa,
					textFeedbackEnabled: state.pecCycle.textFeedbackEnabled,
					emailTemplateId: state.pecCycle.emailTemplateId,
					reminderTemplateId: state.pecCycle.reminderTemplateId,
					reminderDays: state.pecCycle.reminderDays,
					reminderLogic: state.pecCycle.reminderLogic,
					reminderMaxEmails: state.pecCycle.reminderMaxEmails,
				}
			},

			useCreatePecCycle: () => {
				const queryClient = useQueryClient()
				const toast = useToast()
				const navigate = useNavigate()
				const pecCycle = get().pecCycle

				const createMutation = useMutation({
					...trpc.client.pec.create.mutationOptions(),
					onSuccess: (data) => {
						toast.showToast('PEC cycle created successfully')
						navigate(`/pec/${data.id}/general`)
					},
					onError: (err) => {
						const message = err instanceof Error ? err.message : 'Error creating PEC cycle'
						toast.showToast(message, 'error')
					},
					onSettled: () => {
						queryClient.invalidateQueries({ queryKey: [['pec']] })
					},
				})

				return {
					createPecCycle: async (data: PecFormData) => {
						if (!pecCycle?.organizationId) return
						await createMutation.mutateAsync({
							organizationId: pecCycle.organizationId,
							name: data.name || '',
							description: data.description || undefined,
							populationId: data.populationId || undefined,
							promptText: data.promptText || '',
							ratingScale: data.ratingScale || 5,
							emailTemplateId: data.emailTemplateId || undefined,
						})
					},
					isLoading: createMutation.isPending,
				}
			},

			useEditPecCycle: (pecCycleId?: string) => {
				const queryClient = useQueryClient()
				const toast = useToast()
				const pecCycle = get().pecCycle

				const editMutation = useMutation({
					...trpc.client.pec.update.mutationOptions(),
					onSuccess: () => {
						toast.showToast('PEC cycle updated successfully')
					},
					onError: (err) => {
						const message = err instanceof Error ? err.message : 'Error updating PEC cycle'
						toast.showToast(message, 'error')
					},
					onSettled: () => {
						queryClient.invalidateQueries({
							queryKey: trpc.client.pec.search.queryKey(),
							exact: false,
							refetchType: 'all',
						})
						queryClient.invalidateQueries({
							queryKey: trpc.client.pec.getById.queryKey({ id: pecCycleId }),
							exact: false,
							refetchType: 'all',
						})
					},
				})

				return {
					editPecCycle: async (data: Partial<PecFormData>) => {
						if (!pecCycle?.id || !pecCycle?.organizationId) return
						await editMutation.mutateAsync({
							id: pecCycle.id,
							organizationId: pecCycle.organizationId,
							name: data.name,
							description: data.description ?? undefined,
							populationId: data.populationId ?? undefined,
							promptText: data.promptText,
							ratingScale: data.ratingScale,
							ratingConfig: data.ratingConfig ?? undefined,
							allowNa: data.allowNa,
							textFeedbackEnabled: data.textFeedbackEnabled,
							emailTemplateId: data.emailTemplateId ?? undefined,
							reminderTemplateId: data.reminderTemplateId ?? undefined,
							reminderDays: data.reminderDays,
							reminderLogic: data.reminderLogic ?? undefined,
							reminderMaxEmails: data.reminderMaxEmails,
						})
					},
					isLoading: editMutation.isPending,
				}
			},

			useMarkReadyPecCycle: (pecCycleId: string) => {
				const queryClient = useQueryClient()
				const toast = useToast()

				const markReadyMutation = useMutation({
					...trpc.client.pec.markReady.mutationOptions(),
					onSuccess: () => {
						toast.showToast('PEC cycle marked as ready')
						// Update local state immediately to show status change
						const latestPecCycle = get().pecCycle
						if (latestPecCycle) {
							get().setPecCycle({ ...latestPecCycle, status: 'ready' })
						}
					},
					onError: (err) => {
						const message = err instanceof Error ? err.message : 'Error marking PEC cycle as ready'
						toast.showToast(message, 'error')
					},
					onSettled: () => {
						queryClient.invalidateQueries({ queryKey: [['pec']] })
					},
				})

				return {
					markReadyPecCycle: async () => {
						const pecCycle = get().pecCycle
						if (!pecCycle?.organizationId) return
						await markReadyMutation.mutateAsync({
							id: pecCycleId,
							organizationId: pecCycle.organizationId,
						})
					},
					isLoading: markReadyMutation.isPending,
				}
			},

			useLaunchPecCycle: (pecCycleId: string) => {
				const queryClient = useQueryClient()
				const toast = useToast()

				const launchMutation = useMutation({
					...trpc.client.pec.launch.mutationOptions(),
					onSuccess: () => {
						toast.showToast('PEC cycle launched successfully')
						// Update local state immediately to show status change
						const latestPecCycle = get().pecCycle
						if (latestPecCycle) {
							get().setPecCycle({
								...latestPecCycle,
								status: 'active',
								launchedAt: new Date(),
							})
						}
					},
					onError: (err) => {
						const message = err instanceof Error ? err.message : 'Error launching PEC cycle'
						toast.showToast(message, 'error')
					},
					onSettled: () => {
						queryClient.invalidateQueries({ queryKey: [['pec']] })
					},
				})

				return {
					launchPecCycle: async () => {
						const pecCycle = get().pecCycle
						if (!pecCycle?.organizationId) return
						await launchMutation.mutateAsync({
							id: pecCycleId,
							organizationId: pecCycle.organizationId,
							emailTemplateId: pecCycle.emailTemplateId ?? undefined,
						})
					},
					isLoading: launchMutation.isPending,
				}
			},

			useClosePecCycle: (pecCycleId: string) => {
				const queryClient = useQueryClient()
				const toast = useToast()

				const closeMutation = useMutation({
					...trpc.client.pec.close.mutationOptions(),
					onSuccess: () => {
						toast.showToast('PEC cycle closed')
						// Update local state immediately to show status change
						const latestPecCycle = get().pecCycle
						if (latestPecCycle) {
							get().setPecCycle({
								...latestPecCycle,
								status: 'closed',
								closedAt: new Date(),
							})
						}
					},
					onError: (err) => {
						const message = err instanceof Error ? err.message : 'Error closing PEC cycle'
						toast.showToast(message, 'error')
					},
					onSettled: () => {
						queryClient.invalidateQueries({ queryKey: [['pec']] })
					},
				})

				return {
					closePecCycle: async () => {
						const pecCycle = get().pecCycle
						if (!pecCycle?.organizationId) return
						await closeMutation.mutateAsync({
							id: pecCycleId,
							organizationId: pecCycle.organizationId,
						})
					},
					isLoading: closeMutation.isPending,
				}
			},

			useArchivePecCycle: (pecCycleId: string) => {
				const queryClient = useQueryClient()
				const toast = useToast()

				const archiveMutation = useMutation({
					...trpc.client.pec.archive.mutationOptions(),
					onSuccess: () => {
						toast.showToast('PEC cycle archived')
						// Update local state immediately to show status change
						const latestPecCycle = get().pecCycle
						if (latestPecCycle) {
							get().setPecCycle({ ...latestPecCycle, status: 'archived' })
						}
					},
					onError: (err) => {
						const message = err instanceof Error ? err.message : 'Error archiving PEC cycle'
						toast.showToast(message, 'error')
					},
					onSettled: () => {
						queryClient.invalidateQueries({ queryKey: [['pec']] })
					},
				})

				return {
					archivePecCycle: async () => {
						const pecCycle = get().pecCycle
						if (!pecCycle?.organizationId) return
						await archiveMutation.mutateAsync({
							id: pecCycleId,
							organizationId: pecCycle.organizationId,
						})
					},
					isLoading: archiveMutation.isPending,
				}
			},
		})),
		{ name: 'pec-store' },
	),
)
